// Content > "Ask our team" (doc 28 W14, claim C32). A client asks the
// UpRyzze team for a change or a new piece; the request lands in the team's
// task queue and the list below shows where it stands.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { StatusPill, Surface } from '../primitives';
import { newClientUuid } from '../../lib/outboxStore';
import {
  createContentRequest,
  listContentRequests,
} from '../../services/contentRequestService';

const TYPES = [
  { value: 'change', label: 'Change to a page' },
  { value: 'new_page', label: 'New page' },
  { value: 'new_post', label: 'New blog post' },
  { value: 'other', label: 'Something else' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));

const STATUS = {
  received: { kind: 'info', label: 'Received' },
  in_progress: { kind: 'warning', label: 'In progress' },
  done: { kind: 'success', label: 'Done' },
  closed: { kind: 'neutral', label: 'Closed' },
};

const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : '';

export default function ContentRequestsPanel() {
  const [type, setType] = useState('change');
  const [page, setPage] = useState('');
  const [note, setNote] = useState('');
  const [items, setItems] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  // One id per draft, so a double click or a retry never files it twice.
  // Editing the draft makes it a new request (a new id).
  const uuid = useRef(newClientUuid());
  const edit = (setter) => (e) => {
    uuid.current = newClientUuid();
    setter(e.target.value);
  };

  const load = useCallback(() => {
    listContentRequests()
      .then((r) => setItems(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => {});
  }, []);
  useEffect(load, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSent(false);
    setSending(true);
    try {
      await createContentRequest({
        type,
        page: page.trim(),
        note: note.trim(),
        clientUuid: uuid.current,
      });
      uuid.current = newClientUuid();
      setNote('');
      setPage('');
      setSent(true);
      load();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Could not send the request. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ px: 3, py: 2.5, maxWidth: 760 }}>
      <Surface sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Ask our team
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Tell us what you want changed or written. Our team picks it up, ships
          it, and marks it done here.
        </Typography>
        <Stack component="form" spacing={2} onSubmit={submit}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="What do you need?"
              value={type}
              onChange={edit(setType)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Page (optional)"
              placeholder="e.g. Services, or /about"
              value={page}
              onChange={edit(setPage)}
              size="small"
              fullWidth
              inputProps={{ maxLength: 300 }}
            />
          </Stack>
          <TextField
            label="Details"
            value={note}
            onChange={edit(setNote)}
            multiline
            minRows={4}
            fullWidth
            required
            inputProps={{ maxLength: 2000 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
          {sent && (
            <Alert severity="success">
              Sent. You will see it move to Done here when it is live.
            </Alert>
          )}
          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={sending || note.trim().length < 5}
              sx={{ textTransform: 'none' }}
            >
              {sending ? 'Sending...' : 'Send request'}
            </Button>
          </Box>
        </Stack>
      </Surface>

      {items.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Your requests
          </Typography>
          <Stack spacing={1}>
            {items.map((r) => {
              const s = STATUS[r.status] || STATUS.received;
              return (
                <Surface key={r.id} sx={{ p: 1.75 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {TYPE_LABEL[r.type] || 'Request'}
                        {r.page ? `: ${r.page}` : ''}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {r.note}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                      >
                        Sent {fmt(r.requestedAt)}
                        {r.doneAt ? `, done ${fmt(r.doneAt)}` : ''}
                      </Typography>
                    </Box>
                    <StatusPill kind={s.kind} label={s.label} size="sm" />
                  </Stack>
                </Surface>
              );
            })}
          </Stack>
        </>
      )}
    </Box>
  );
}
